<template>
  <div class="header">
    <div class="header__row header__row--second" :class="'header__home'">
      <nuxt-link to="/">
        <NuxtImg format="webp" densities="x1" class="header__link" :src="logos.logoUrl"/>
      </nuxt-link>
      <nav @click.stop class="nav">
        <ul class="nav__list">
          <li class="nav__item nav__item-burger" @click="toggleModalDrop">
            <IconsBurgerMenuIcon/>
            {{ $t("header.menu") }}
          </li>
        </ul>
      </nav>

      <div class="header__info">
        <div v-if="activeCity" @click="toggleCityDropdown" class="header__city" ref="cityDropdownRef">
          <IconsLocationIcon/>
          {{ activeCity.name[0][locale] }}
          <IconsArrowDown class="header__city-icon" v-if="!cityDropdownVisible"/>
          <IconsArrowUp v-else class="header__city-icon--red"/>

          <div v-if="cityDropdownVisible" class="header__city-dropdown">
            <div class="header__city-dropdown-title" v-for="city in cities" :key="city.id" @click="selectCity(city)">
              {{ city.name[0][locale] }}
            </div>
          </div>
        </div>

        <NuxtLink v-if="activeCity" class="header__number" :href="'tel:' + activeCity?.contacts[0]?.phone">
          {{ activeCity?.contacts[0]?.phone }}
        </NuxtLink>

        <div class="header__buttons">
          <button @click="toggleModal($event)" class="header__button button button--secondary">
            {{ $t("callback.callback") }}
          </button>
          <HeaderModalCallback
            v-if="modalCallbackVisible"
            @close-modal-with-error="closeModalWithError"
            @close-modal="closeModal"
            ref="callbackModal"
          />
          <div class="header__lang">
            <button
              class="lang__button"
              :class="{ 'lang__button--active': locale === 'ru' }"
              @click="setActive('ru')"
            >
              Ru
            </button>
            <button
              class="lang__button"
              :class="{ 'lang__button--active': locale === 'en' }"
              @click="setActive('en')"
            >
              En
            </button>
          </div>
          <div @click="showDropdown" class="header__burger">
            <IconsBurgerMenuIcon :color="'#ed1c24'"/>
          </div>
        </div>
      </div>
    </div>
    <FormMessage
      v-if="isSuccessMessageVisible"
      :is-close-callback-modal-with-error="isCloseCallbackModalWithError"
      @close="isSuccessMessageVisible=false"
    />
  </div>
  <HeaderDropdown
    v-if="dropdownVisible && cities"
    @close-dropdown="closeDropdown"
    @change-city="closeDropdown"
    :phone="activeCity?.contacts[0]?.phone"
    :contacts="cities"
    :activeCity="activeCity"
    ref="dropdownModal"
  />
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useLocalesStore } from '~/stores/locales/locales';
import { useCitiesStore } from '~/stores/cities/cities';
import { useLogosStore } from '~/stores/logos/logos';

const cityDropdownRef = ref(null)
const lastScrollY = ref(0);
const showFirstRow = ref(true);
const modalCallbackVisible = ref(false);
const dropdownVisible = ref(false)
const cityDropdownVisible = ref(false)
const isSuccessMessageVisible = ref(false)
const { cities } = storeToRefs(useCitiesStore());
const { activeCity } = storeToRefs(useCitiesStore());
const callbackModal = ref(null);
const dropdownModal = ref(null);
const isCloseCallbackModalWithError = ref(false);
const { logos } = storeToRefs(useLogosStore());
const { locale } = storeToRefs(useLocalesStore());

const citiesStore = useCitiesStore();

const toggleCityDropdown = () => {
  cityDropdownVisible.value = !cityDropdownVisible.value;
};

const selectCity = (city) => {
  cityDropdownVisible.value = false
  citiesStore.setActiveCity(city);
}

const setActive = (lang) => {
  useLocalesStore().setLocale(lang);
}

const showDropdown = () => {
  dropdownVisible.value = !dropdownVisible.value;
  if (!dropdownVisible.value) {
    document.body.style.overflow = "";
  }
  modalCallbackVisible.value = false;
};
const closeDropdown = () => {
  dropdownVisible.value = false;
};
const handleScroll = () => {
  const currentScrollY = window.scrollY;
  showFirstRow.value = currentScrollY <= lastScrollY.value;
  lastScrollY.value = currentScrollY;
};
const toggleModal = (event) => {
  event.stopPropagation();
  cityDropdownVisible.value = false
  if (modalCallbackVisible.value) {
    modalCallbackVisible.value = false;
    document.removeEventListener("click", handleClickOutside);
  } else {
    modalCallbackVisible.value = true;
    dropdownVisible.value = false;
    document.addEventListener("click", handleClickOutside);
  }
};
const toggleModalDrop = () => {
  cityDropdownVisible.value = false
  if (dropdownVisible.value) {
    dropdownVisible.value = false;
    document.removeEventListener("click", handleClickOutsideDrop);
    document.body.style.overflow = "";
  } else {
    dropdownVisible.value = true;
    modalCallbackVisible.value = false;
    document.addEventListener("click", handleClickOutsideDrop);
  }
};
const handleClickOutside = (event) => {
  const modal = callbackModal.value?.$el;
  if (modal && !modal.contains(event.target)) {
    toggleModal(event);
  }
};
const handleClickOutsideDrop = (event) => {
  const modal = dropdownModal.value?.$el;
  if (modal && !modal.contains(event.target)) {
    toggleModalDrop(event);
  }
};
const closeModal = () => {
  modalCallbackVisible.value = false;
  isSuccessMessageVisible.value = true;
  setTimeout(() => {
    isSuccessMessageVisible.value = false;
  }, 50000);
};
const closeModalWithError = () => {
  modalCallbackVisible.value = false;
  isSuccessMessageVisible.value = true;
  isCloseCallbackModalWithError.value = true;
  setTimeout(() => {
    isSuccessMessageVisible.value = false;
  }, 50000);
};

const handleClickOutsideCityDropdown = (event) => {
  if (cityDropdownRef.value && !cityDropdownRef.value.contains(event.target)) {
    cityDropdownVisible.value = false;
  }
};

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('click', handleClickOutsideCityDropdown);
  }
});

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('scroll', handleScroll);
    document.removeEventListener('click', handleClickOutsideCityDropdown);
  }
});
</script>

<style lang="scss" scoped>
.nav {
  display: none;

  @media (min-width: $screen-m) {
    display: flex;
    margin-right: auto;
  }
}

.nav__list {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 30px;

  @media (min-width: $screen-m) and (max-width: $screen-l - 1) {
    gap: 0;
    padding: 0;
  }

  @media (min-width: $screen-l) {
    gap: 24px;
    padding: 0;
  }

  @media (min-width: $screen-xl) {
    gap: 32px;
    padding: 0 24px;
  }
}

.nav__item {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  font-size: 14px;
  line-height: 171%;
  color: #282828;

  @media (min-width: $screen-m) and (max-width: $screen-l - 1) {
    display: none;
  }

  @media (min-width: $screen-l) {
    font-size: 16px;
    line-height: 150%;
  }
}

.nav__item-burger {
  display: none;

  @media (min-width: $screen-m) {
    position: relative;
    right: 20px;
    display: flex;
  }
}

.header {
  display: flex;
  flex-direction: column;
  max-width: 1440px;
  margin: 0 auto;
}

.header__row {
  display: flex;
  justify-content: space-between;

  &--first {
    padding: 12px 0 10px;
    align-items: center;
    border-bottom: 1px solid #ccc;
  }
}

.header__home {
  position: relative;
  padding: 6px 0 16px;

  @media (min-width: $screen-m) {
    padding: 3px 0;
  }

  @media (min-width: $screen-l) {
    padding: 36px 0;
  }
}

.header__city-dropdown {
  position: absolute;
  width: fit-content;
  border-radius: 8px;
  border: none;
  background-color: white;
  z-index: 10;
  top: 32px;
  right: 0;
  box-shadow: 0px 4px 6px 0px rgba(0, 0, 0, 0.06);
}

.header__city-dropdown-title {
  cursor: pointer;
  padding: 16px 16px 0;
  color: #282828;
  font-size: 12px;
  width: max-content;

  @media (min-width: $screen-l) {
    font-size: 14px;
  }

  &:hover {
    color: #ed1c24;
  }

  &:last-of-type {
    padding-bottom: 16px;
  }
}

.header__link {
  display: block;
  width: 120px;

  @media (min-width: $screen-m) {
    margin-right: 50px;
    margin-top: 5px;
  }

  @media (min-width: $screen-l) {
    margin-right: 40px;
    margin-top: 0;
    width: 165px;
  }
}

.header__back {
  font-weight: 500;
  font-size: 12px;
  line-height: 140%;
  color: #7a7a7a;

  @media (min-width: $screen-m) {
    font-size: 14px;
    line-height: 146%;
  }
}

.header__info {
  display: flex;
  align-items: center;
  gap: 24px;
}

.header__city {
  position: relative;
  cursor: pointer;
  display: none;
  gap: 8px;
  color: #7a7a7a;
  font-weight: 500;
  font-size: 16px;
  line-height: 152%;

  @media (min-width: $screen-l) {
    display: flex;
    align-items: center;
  }

  &--visible {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &--unvisible {
    display: none;
  }

  &--second {
    font-weight: 500;
    font-size: 12px;
    line-height: 140%;
    color: #7a7a7a;

    @media (min-width: $screen-m) {
      font-size: 14px;
      line-height: 146%;
    }
  }
}

.header__city-icon {
  color: #7a7a7a;
  width: 10px;
  height: 10px;

  &--red {
    width: 10px;
    height: 10px;
    color: #ed1c24;
  }
}

.header__number {
  display: none;

  @media (min-width: $screen-m) {
    display: block;
    color: #7a7a7a;
    font-weight: 500;
    font-size: 14px;
    line-height: 150%;
  }

  @media (min-width: $screen-l) {
    font-size: 16px;
  }
}

.header__buttons {
  display: flex;
}

.header__button {
  display: none;

  @media (min-width: $screen-m) {
    display: block;
  }
}

.header__lang {
  display: flex;
  gap: 16px;
  margin: 0 24px;

  @media (min-width: $screen-m) {
    margin: 0 0 0 24px;
  }
}

.lang__button {
  font-weight: 500;
  font-size: 14px;
  line-height: 146%;
  color: #272727;

  @media (min-width: $screen-m) {
    font-size: 16px;
    line-height: 152%;
  }

  &--active {
    color: #ed1c24;
    text-decoration: underline;
    text-underline-offset: 4px;
  }
}

.header__burger {
  cursor: pointer;
  width: 32px;
  height: 32px;
  padding: 8px;
  border-radius: 50%;
  background: #ffe7e7;
  display: flex;
  align-items: center;

  @media (min-width: $screen-m) {
    display: none;
  }
}
</style>
